import type { InstallationDto, ScheduleEventDto, SurveyDto } from '../../../lib/api/v2/planning'
import { hoursPerWorkDay } from './planning-settings'
import { brusselsTodayIso } from './planning-helpers'

/**
 * Reading a WORK ITEM as a set of créneaux — SOLY-PLAN-003 MS-08, then SOLY-PLAN-006 MS-04.
 *
 * Was `pose-slots.ts` until the métré gained the same shape. Mirrors the backend `WorkItemSlots`:
 * two named readers, one per work item, and no `kind` parameter — a switch missing the new branch
 * compiles and falls into its default.
 *
 * ⚠️ The two are NOT symmetric below the surface, and the asymmetry is the decision, not an
 * oversight: a pose carries a BUDGET its créneaux consume (décision 7 of PLAN-003), a métré does
 * not (décision D2 of PLAN-006 — the estimate is how long ONE visit takes, and there is no
 * remainder to be left of). So `poseBudget` has no `surveyBudget` twin, on purpose.
 *
 * Original note, still true of the pose half — SOLY-PLAN-003 MS-08.
 *
 * The estimate stopped being the placed duration and became a BUDGET the créneaux consume
 * (décision 7, backend MS-05). The fiche and the agenda both need to say how much of it is placed,
 * so the arithmetic lives here once, pure and unit-tested, instead of inside two components.
 *
 * The day → hours conversion comes from the backend (planning-settings): the same number the
 * server used to compute `remainingHours` on the Inbox. Two divisors would drift.
 */

/** Live (non-cancelled) créneaux of a pose, chronological — the order the fiche numbers them in. */
export function slotsOfPose(events: readonly ScheduleEventDto[], installationId: string): ScheduleEventDto[] {
  return live(events, (e) => e.installationId === installationId)
}

/**
 * Live créneaux of a MÉTRÉ, chronological — SOLY-PLAN-006 MS-04. Same contract as
 * {@link slotsOfPose}: a cancelled créneau is not a créneau.
 */
export function slotsOfSurvey(events: readonly ScheduleEventDto[], technicalSurveyId: string): ScheduleEventDto[] {
  return live(events, (e) => e.technicalSurveyId === technicalSurveyId)
}

/** The single core both readers reduce to — private, so nobody invents a third « live slot ». */
function live(events: readonly ScheduleEventDto[], belongsTo: (e: ScheduleEventDto) => boolean): ScheduleEventDto[] {
  return events
    .filter((e) => belongsTo(e) && !e.cancelledAt)
    .sort((a, b) => `${a.startDate} ${a.startTime ?? ''}`.localeCompare(`${b.startDate} ${b.startTime ?? ''}`))
}

export function toHours(unit: 'Days' | 'Hours', value: number): number {
  return unit === 'Days' ? value * hoursPerWorkDay() : value
}

export interface PoseBudget {
  /** Hours placed on the calendar across every live créneau. */
  plannedHours: number
  /**
   * Hours the estimate budgets — `null` when nobody has estimated the pose (SOLY-PLAN-004).
   * NOT zero: « rien d'estimé » and « zéro heure » are different answers, and a 0 would make every
   * placed hour read as an overrun.
   */
  estimatedHours: number | null
  /** Hours left to place, floored at zero. Always 0 with no budget — there is nothing to be left of. */
  remainingHours: number
  /** Hours placed beyond the budget. Information, never a warning (décision 7). Always 0 with no budget. */
  overHours: number
  slotCount: number
}

export function poseBudget(pose: Pick<InstallationDto, 'id' | 'estimatedDurationUnit' | 'estimatedDurationValue'>,
  events: readonly ScheduleEventDto[]): PoseBudget {
  const slots = slotsOfPose(events, pose.id)
  const plannedHours = slots.reduce((sum, s) => sum + toHours(s.durationUnit, s.durationValue), 0)
  const estimatedHours = pose.estimatedDurationValue === null
    ? null
    : toHours(pose.estimatedDurationUnit, pose.estimatedDurationValue)
  return {
    plannedHours,
    estimatedHours,
    // Both comparisons are guarded: with no budget there is neither « il reste » nor « au-delà de
    // l'estimation », and computing them against 0 would have announced an overrun on the first
    // hour placed. Mirrors the domain (WorkItemBudget).
    remainingHours: estimatedHours === null ? 0 : Math.max(0, estimatedHours - plannedHours),
    overHours: estimatedHours === null ? 0 : Math.max(0, plannedHours - estimatedHours),
    slotCount: slots.length,
  }
}

/**
 * Hours rendered the way the user wrote the estimate: « 2 j » when it divides into whole days and
 * the estimate itself is in days, « 4 h » otherwise. Mixed durations are the normal case here
 * (« 1 jour + 4 h » is how a day and a half is said), so a remainder of 12 h against a Days
 * estimate reads « 1,5 j » rather than a number of hours nobody typed.
 */
export function formatDuration(hours: number, unit: 'Days' | 'Hours'): string {
  if (hours === 0) return unit === 'Days' ? '0 j' : '0 h'
  if (unit === 'Hours') return `${trim(hours)} h`
  const days = hours / hoursPerWorkDay()
  return `${trim(days)} j`
}

/** Drops the trailing « ,0 » — « 2 j » not « 2,0 j » — and uses the FR decimal comma. */
function trim(n: number): string {
  return Number(n.toFixed(2)).toString().replace('.', ',')
}

/**
 * Whether the pose still accepts a new créneau. Mirrors the DOMAIN rule
 * (Installation.EnsureCanReceiveSlot + MS-07): once the report is written the pose is closed and
 * the only remaining gesture is « créer une finition ». The UI hides what the backend refuses —
 * it does not invent a second rule.
 */
export function poseAcceptsNewSlot(pose: Pick<InstallationDto, 'status' | 'reportSummary'>): boolean {
  return pose.status === 'Open' || (pose.status === 'InProgress' && pose.reportSummary === null)
}

/**
 * Whether the métré still accepts a new créneau. Mirrors the DOMAIN rule
 * (`TechnicalSurvey.EnsureCanReceiveSlot`, SOLY-PLAN-006 MS-01) — the UI hides what the backend
 * refuses, it does not invent a second rule.
 *
 * 🔴 NOT the mirror of {@link poseAcceptsNewSlot}, and the difference is décision D3: a **Completed**
 * métré accepts one more créneau. « Avec ou sans rapport, on doit pouvoir mettre un autre créneau »
 * (Thierry, 2026-09-15). On a pose the report arbitrates between two competing gestures — one more
 * créneau, or a FINITION, which is a new work item and a new commercial cycle. A métré has no
 * competing object, so closing the door would arbitrate nothing: it would only send the user back
 * to the four wrong encodings this feature exists to remove.
 *
 * What closes the door is `Cancelled` / `NotApplicable` — and there, the user reads why.
 */
export function surveyAcceptsNewSlot(survey: Pick<SurveyDto, 'status'>): boolean {
  return survey.status !== 'Cancelled' && survey.status !== 'NotApplicable'
}

/**
 * Whether the work item still has a passage TO COME — SOLY-PLAN-006.
 *
 * 🔴 Compared in CALENDAR DAYS, never in instants, for the same reason M6 is (`IsCoveredByReport`
 * côté backend, invariant 1 de `_constraints.md`) : un métré de 9h à 11h n'est pas « passé » à
 * 11h01, il l'est le lendemain. Comparer des instants ferait basculer le badge de la section à
 * l'heure où le métreur est encore sur place.
 *
 * `endDate` is the last day the slot occupies (inclusive, SOLY-PLAN-007), so the whole rule is a
 * comparison of two « yyyy-MM-dd » — the slot's last day against today in Brussels.
 */
export function hasUpcomingSlot(slots: readonly ScheduleEventDto[], now: Date = new Date()): boolean {
  const today = brusselsTodayIso(now)
  return slots.some((s) => s.endDate >= today)
}

/**
 * Whether « Métré non nécessaire » is still a legal move. Mirrors
 * `TechnicalSurvey.MarkNotApplicable`, which throws on `Cancelled` AND on `Completed`.
 *
 * 🔴 Deliberately NOT {@link surveyAcceptsNewSlot}, and the difference is the bug this closes:
 * décision D3 stopped `Completed` being a door for CRÉNEAUX, it did not re-open the LIFECYCLE
 * transitions. Driving the button off « is the section open » offered the gesture on a reported
 * métré and the backend answered INVALID_OPERATION with a raw English domain message.
 *
 * Two questions, two predicates — the UI mirrors what the backend refuses, it does not guess.
 */
export function surveyAcceptsNotApplicable(survey: Pick<SurveyDto, 'status'>): boolean {
  return survey.status === 'Open'
}
